import React, { createContext, useContext, useEffect, useState } from 'react';
import { KpiDefinition } from '../interfaces/athlete';
import { getKpiDefinitions } from '../services/kpi';

interface KpiSettingsContextType {
    kpiDefinitions: KpiDefinition[];
    kpiSettingsLoaded: boolean;
    refreshKpiSettings: () => void;
    getActiveKpis: () => KpiDefinition[];
    getKpiByName: (kpiName: string) => KpiDefinition | undefined;
    getKpiLabel: (kpiName: string) => string;
    getKpiLabelWithUnit: (kpiName: string) => string;
}

const KpiSettingsContext = createContext<KpiSettingsContextType>({
    kpiDefinitions: [],
    kpiSettingsLoaded: false,
    refreshKpiSettings: () => {},
    getActiveKpis: () => [],
    getKpiByName: () => undefined,
    getKpiLabel: () => '',
    getKpiLabelWithUnit: () => ''
});

export const useKpiSettings = () => useContext(KpiSettingsContext);

export const KpiSettingsProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
    const [kpiDefinitions, setKpiDefinitions] = useState<KpiDefinition[]>([]);
    const [kpiSettingsLoaded, setKpiSettingsLoaded] = useState(false);

    const loadKpiSettings = () => {
        setKpiSettingsLoaded(false);
        getKpiDefinitions()
            .then(response => {
                // Response format from settings endpoint: { setting_name, value }
                if (response.data && response.data.value) {
                    // Add local IDs to each KPI for table management
                    const kpisWithIds = response.data.value.map((kpi: any, index: number) => ({
                        ...kpi,
                        id: index + 1 // Local ID for UI management
                    }));
                    setKpiDefinitions(kpisWithIds);
                } else {
                    // If no settings exist yet, start with empty array
                    setKpiDefinitions([]);
                }
                setKpiSettingsLoaded(true);
            })
            .catch(error => {
                setKpiDefinitions([]);
                setKpiSettingsLoaded(true); // Mark as loaded even on error to prevent infinite loading
            });
    };

    useEffect(() => {
        loadKpiSettings();
    }, []);

    const getActiveKpis = () => {
        return kpiDefinitions.filter(kpi => kpi.is_active);
    };

    const getKpiByName = (kpiName: string) => {
        return kpiDefinitions.find(kpi => kpi.kpi_name === kpiName);
    };

    const getKpiLabel = (kpiName: string) => {
        const kpi = getKpiByName(kpiName);
        return kpi ? kpi.display_name : kpiName;
    };

    const getKpiLabelWithUnit = (kpiName: string) => {
        const kpi = getKpiByName(kpiName);
        if (!kpi) return kpiName;
        return kpi.unit ? `${kpi.display_name} (${kpi.unit})` : kpi.display_name;
    };

    return (
        <KpiSettingsContext.Provider value={{
            kpiDefinitions,
            kpiSettingsLoaded,
            refreshKpiSettings: loadKpiSettings,
            getActiveKpis,
            getKpiByName,
            getKpiLabel,
            getKpiLabelWithUnit
        }}>
            {children}
        </KpiSettingsContext.Provider>
    );
};
